import { useCallback, useEffect, useRef, useState } from 'react';
import firebase from 'firebase/compat/app';
import { getCountFromServer, Query as ModularQuery } from 'firebase/firestore';
import useAuth from 'hooks/useAuth';
import { dispatch } from 'store';
import { setLastSaved } from 'store/reducers/ui';
import { EventTaskIndexItem } from 'types/eventTaskIndex';
import { offset } from 'utils/functions';
const DEFAULT_PAGE_SIZE = 20;
const TASK_INDEX_COLLECTION = 'eventTaskIndex';

type TaskIndexDoc = firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>;

interface TaskIndexPage {
  items: EventTaskIndexItem[];
  cursor: TaskIndexDoc | null;
  hasNext: boolean;
}

interface UseEventTaskIndexArgs {
  assignedToContactId?: string | null;
  dueDateSortGte?: Date | null;
  enabled?: boolean;
  pageSize?: number;
}

export interface UseEventTaskIndexGridArgs {
  assignedToContactId?: string | null;
  dueDateSortGte?: Date | null;
  enabled?: boolean;
  page: number;
  pageSize: number;
  refreshKey?: number;
}

function getTaskIndexQueryError(error: unknown, context: Record<string, unknown>): Error {
  const firestoreError = error as { code?: string; message?: string } | null;
  const code = firestoreError?.code || 'unknown';
  const message = firestoreError?.message || String(error);

  console.error('eventTaskIndex query failed', {
    code,
    message,
    ...context,
    error
  });

  if (code === 'failed-precondition') {
    return new Error('Tasks query needs a Firestore index. Check console for details.');
  }

  if (code === 'permission-denied') {
    return new Error('You do not have access to read tasks yet. Check console for details.');
  }

  if (code === 'unavailable') {
    return new Error('Tasks are temporarily unavailable. Check console for details.');
  }

  return new Error('Unable to load tasks. Check console for details.');
}

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toOffsetDate(value: any): Date | null {
  const date = toDate(value);
  return date ? offset(date) : null;
}

function mapTaskIndexDoc(doc: TaskIndexDoc): EventTaskIndexItem {
  const data = doc.data();
  return {
    id: doc.id,
    indexId: data.indexId || doc.id,
    eventId: data.eventId || '',
    taskId: data.taskId || '',
    source: data.source,
    title: data.title || '',
    category: data.category || '',
    dueDate: toDate(data.dueDate),
    dueDateSort: toDate(data.dueDateSort) || new Date('9999-12-31T00:00:00.000Z'),
    assignedTo: data.assignedTo || {
      contactId: data.assignedToContactId || '',
      name: data.assignedToName || ''
    },
    assignedToContactId: data.assignedToContactId || data.assignedTo?.contactId || '',
    assignedToName: data.assignedToName || data.assignedTo?.name || '',
    finalized: data.finalized === true,
    notApplicable: data.notApplicable === true,
    isOpen: data.isOpen === true,
    dateStart: toOffsetDate(data.dateStart),
    isFutureEvent: data.isFutureEvent === true,
    eventTitle: data.eventTitle || '',
    eventDisplayTitle: data.eventDisplayTitle || data.eventTitle || 'Untitled Event',
    clTypeofEvent: data.clTypeofEvent || '',
    entTypeName: data.entTypeName || '',
    venueName: data.venueName || '',
    valueLabel: data.valueLabel || null,
    note: data.note || null,
    updatedAt: toDate(data.updatedAt)
  };
}

function createTaskIndexBaseQuery(
  db: firebase.firestore.Firestore,
  assignedToContactId?: string | null,
  dueDateSortGte?: Date | null
) {
  let query: firebase.firestore.Query = db
    .collection(TASK_INDEX_COLLECTION)
    .where('isOpen', '==', true)
    .where('isFutureEvent', '==', true);

  if (assignedToContactId) {
    query = query.where('assignedToContactId', '==', assignedToContactId);
  }

  if (dueDateSortGte) {
    query = query.where('dueDateSort', '>=', dueDateSortGte);
  }

  return query;
}

function createOrderedTaskIndexQuery(
  db: firebase.firestore.Firestore,
  assignedToContactId?: string | null,
  dueDateSortGte?: Date | null,
  cursor?: TaskIndexDoc | null
) {
  let query = createTaskIndexBaseQuery(db, assignedToContactId, dueDateSortGte)
    .orderBy('dueDateSort', 'asc')
    .orderBy('dateStart', 'asc');

  if (cursor) {
    query = query.startAfter(cursor);
  }

  return query;
}

function toModularQuery(query: firebase.firestore.Query): ModularQuery<unknown> {
  return ((query as any)._delegate || query) as ModularQuery<unknown>;
}

export function useEventTaskIndex({
  assignedToContactId,
  dueDateSortGte,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE
}: UseEventTaskIndexArgs) {
  const { db } = useAuth();
  const [pages, setPages] = useState<TaskIndexPage[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createQuery = useCallback(
    (cursor?: TaskIndexDoc | null) => {
      return createOrderedTaskIndexQuery(db, assignedToContactId, dueDateSortGte, cursor).limit(
        pageSize + 1
      );
    },
    [assignedToContactId, db, dueDateSortGte, pageSize]
  );

  const makePage = useCallback(
    (docs: TaskIndexDoc[]): TaskIndexPage => {
      const visibleDocs = docs.slice(0, pageSize);
      return {
        items: visibleDocs.map(mapTaskIndexDoc),
        cursor: visibleDocs[visibleDocs.length - 1] || null,
        hasNext: docs.length > pageSize
      };
    },
    [pageSize]
  );

  useEffect(() => {
    if (!enabled) {
      setPages([]);
      setPageIndex(0);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const unsubscribe = createQuery().onSnapshot(
      (snapshot) => {
        setPages([makePage(snapshot.docs)]);
        setPageIndex(0);
        setLoading(false);
      },
      (snapshotError) => {
        setError(
          getTaskIndexQueryError(snapshotError, {
            phase: 'initial-listener',
            assignedToContactId,
            dueDateSortGte,
            pageSize
          })
        );
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [assignedToContactId, createQuery, dueDateSortGte, enabled, makePage, pageSize]);

  const nextPage = useCallback(async () => {
    const currentPage = pages[pageIndex];
    if (!currentPage?.hasNext || !currentPage.cursor) return;

    const cachedPage = pages[pageIndex + 1];
    if (cachedPage) {
      setPageIndex(pageIndex + 1);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const snapshot = await createQuery(currentPage.cursor).get();
      const next = makePage(snapshot.docs);
      setPages((prev) => {
        const copy = [...prev];
        copy[pageIndex + 1] = next;
        return copy;
      });
      setPageIndex(pageIndex + 1);
    } catch (err) {
      setError(
        getTaskIndexQueryError(err, {
          phase: 'next-page',
          assignedToContactId,
          dueDateSortGte,
          pageSize,
          pageIndex,
          cursorId: currentPage.cursor.id
        })
      );
    } finally {
      setLoading(false);
    }
  }, [assignedToContactId, createQuery, dueDateSortGte, makePage, pageIndex, pages, pageSize]);

  const prevPage = useCallback(() => {
    setPageIndex((current) => Math.max(0, current - 1));
  }, []);

  const currentPage = pages[pageIndex];

  return {
    items: currentPage?.items ?? [],
    loading,
    error,
    pageIndex,
    hasNext: currentPage?.hasNext ?? false,
    hasPrev: pageIndex > 0,
    nextPage,
    prevPage
  };
}

export function useMyEventTaskIndex(enabled = true, pageSize = DEFAULT_PAGE_SIZE) {
  const { user } = useAuth();
  return useEventTaskIndex({
    assignedToContactId: user?.contactId || null,
    enabled: enabled && Boolean(user?.contactId),
    pageSize
  });
}

export function useEventTaskIndexGrid({
  assignedToContactId,
  dueDateSortGte,
  enabled = true,
  page,
  pageSize,
  refreshKey = 0
}: UseEventTaskIndexGridArgs) {
  const { db } = useAuth();
  const [items, setItems] = useState<EventTaskIndexItem[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const cursorCacheRef = useRef<Record<number, TaskIndexDoc | null>>({ 0: null });
  const requestIdRef = useRef(0);
  const dueDateSortGteKey = dueDateSortGte?.toISOString() || 'all';
  const queryKey = `${assignedToContactId || ''}:${pageSize}:${dueDateSortGteKey}`;

  useEffect(() => {
    cursorCacheRef.current = { 0: null };
    setItems([]);
    setError(null);
  }, [queryKey, refreshKey]);

  const resolveCursor = useCallback(
    async (targetPage: number) => {
      if (targetPage <= 0) {
        return { cursor: null, pageAvailable: true };
      }

      const cache = cursorCacheRef.current;
      if (targetPage in cache) {
        return { cursor: cache[targetPage], pageAvailable: Boolean(cache[targetPage]) };
      }

      let startPage = targetPage - 1;
      while (startPage > 0 && !(startPage in cache)) {
        startPage -= 1;
      }

      let cursor = cache[startPage] || null;
      for (let pageToLoad = startPage; pageToLoad < targetPage; pageToLoad += 1) {
        const snapshot = await createOrderedTaskIndexQuery(
          db,
          assignedToContactId,
          dueDateSortGte,
          cursor
        )
          .limit(pageSize)
          .get();
        cursor = snapshot.docs[snapshot.docs.length - 1] || null;
        cache[pageToLoad + 1] = cursor;

        if (!cursor) {
          return { cursor: null, pageAvailable: false };
        }
      }

      return { cursor: cache[targetPage] || null, pageAvailable: Boolean(cache[targetPage]) };
    },
    [assignedToContactId, db, dueDateSortGte, pageSize]
  );

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setRowCount(0);
      setPageLoading(false);
      setCountLoading(false);
      setError(null);
      cursorCacheRef.current = { 0: null };
      return undefined;
    }

    let active = true;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPageLoading(true);
    setError(null);

    const run = async () => {
      try {
        const { cursor, pageAvailable } = await resolveCursor(page);
        if (!pageAvailable) {
          if (active && requestId === requestIdRef.current) {
            setItems([]);
          }
          return;
        }

        const snapshot = await createOrderedTaskIndexQuery(
          db,
          assignedToContactId,
          dueDateSortGte,
          cursor
        )
          .limit(pageSize)
          .get();

        if (!active || requestId !== requestIdRef.current) return;

        setItems(snapshot.docs.map(mapTaskIndexDoc));
        cursorCacheRef.current[page + 1] = snapshot.docs[snapshot.docs.length - 1] || null;
      } catch (err) {
        if (!active || requestId !== requestIdRef.current) return;
        setError(
          getTaskIndexQueryError(err, {
            phase: 'grid-page',
            assignedToContactId,
            dueDateSortGte,
            page,
            pageSize
          })
        );
      } finally {
        if (active && requestId === requestIdRef.current) {
          setPageLoading(false);
        }
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [assignedToContactId, db, dueDateSortGte, enabled, page, pageSize, refreshKey, resolveCursor]);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    setCountLoading(true);

    getCountFromServer(
      toModularQuery(createTaskIndexBaseQuery(db, assignedToContactId, dueDateSortGte))
    )
      .then((snapshot) => {
        if (active) {
          setRowCount(snapshot.data().count);
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(
          getTaskIndexQueryError(err, {
            phase: 'grid-count',
            assignedToContactId,
            dueDateSortGte,
            pageSize
          })
        );
      })
      .finally(() => {
        if (active) {
          setCountLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [assignedToContactId, db, dueDateSortGte, enabled, pageSize, queryKey, refreshKey]);

  return {
    items,
    rowCount,
    loading: pageLoading || countLoading,
    error
  };
}

export function useMarkEventTaskFinalized() {
  const { db, user } = useAuth();

  return useCallback(
    async (task: EventTaskIndexItem) => {
      const nextFinalized = !task.finalized;
      const now = new Date();
      const userName = user?.displayName || user?.email || '';

      if (task.source === 'field') {
        await db.runTransaction(async (transaction) => {
          const ref = db.collection('fieldTracking').doc(task.eventId);
          const snap = await transaction.get(ref);
          const data = snap.data() || {};
          const fields = data.fields || {};
          const existing = fields[task.taskId] || {};

          transaction.set(
            ref,
            {
              fields: {
                [task.taskId]: {
                  ...existing,
                  label: existing.label || task.title,
                  category: existing.category || task.category,
                  finalized: nextFinalized,
                  finalizedBy: nextFinalized ? userName : null,
                  finalizedAt: nextFinalized ? now : null,
                  dueDate: existing.dueDate ?? task.dueDate,
                  assignedTo: existing.assignedTo ?? task.assignedTo,
                  notApplicable: false
                }
              },
              updatedAt: now,
              updatedBy: userName
            },
            { merge: true }
          );
        });
      } else if (task.source === 'custom') {
        await db.runTransaction(async (transaction) => {
          const ref = db.collection('fieldTracking').doc(task.eventId);
          const snap = await transaction.get(ref);
          const data = snap.data() || {};
          const customTasks = Array.isArray(data.customTasks) ? data.customTasks : [];
          const updatedTasks = customTasks.map((customTask: any) =>
            customTask.id === task.taskId
              ? {
                  ...customTask,
                  finalized: nextFinalized,
                  finalizedBy: nextFinalized ? userName : null,
                  finalizedAt: nextFinalized ? now : null,
                  notApplicable: false
                }
              : customTask
          );
          transaction.set(
            ref,
            { customTasks: updatedTasks, updatedAt: now, updatedBy: userName },
            { merge: true }
          );
        });
      } else {
        await db.runTransaction(async (transaction) => {
          const ref = db.collection('events').doc(task.eventId);
          const snap = await transaction.get(ref);
          const data = snap.data() || {};
          const flexDetailsFields = Array.isArray(data.flexDetailsFields)
            ? data.flexDetailsFields
            : data.flexDetailsFields
            ? Object.values(data.flexDetailsFields)
            : [];
          const updatedFields = flexDetailsFields.map((field: any) =>
            field.id === task.taskId
              ? {
                  ...field,
                  finalized: nextFinalized,
                  finalizedBy: nextFinalized ? userName : undefined,
                  finalizedAt: nextFinalized ? now : null
                }
              : field
          );
          transaction.update(ref, { flexDetailsFields: updatedFields });
        });
      }

      dispatch(setLastSaved(now));

      if (nextFinalized) {
        try {
          await db.collection(TASK_INDEX_COLLECTION).doc(task.id).delete();
        } catch (err) {
          console.warn('Unable to optimistically remove task index doc', err);
        }
      }
    },
    [db, user]
  );
}

export function useMarkEventTaskNotApplicable() {
  const { db, user } = useAuth();

  return useCallback(
    async (task: EventTaskIndexItem) => {
      const now = new Date();
      const userName = user?.displayName || user?.email || '';
      const nextNotApplicable = !task.notApplicable;

      if (task.source === 'field') {
        await db.runTransaction(async (transaction) => {
          const ref = db.collection('fieldTracking').doc(task.eventId);
          const snap = await transaction.get(ref);
          const data = snap.data() || {};
          const fields = data.fields || {};
          const existing = fields[task.taskId] || {};

          transaction.set(
            ref,
            {
              fields: {
                [task.taskId]: {
                  ...existing,
                  label: existing.label || task.title,
                  category: existing.category || task.category,
                  finalized: nextNotApplicable ? false : existing.finalized ?? false,
                  finalizedBy: nextNotApplicable ? null : existing.finalizedBy ?? null,
                  finalizedAt: nextNotApplicable ? null : existing.finalizedAt ?? null,
                  dueDate: existing.dueDate ?? task.dueDate,
                  assignedTo: existing.assignedTo ?? task.assignedTo,
                  notApplicable: nextNotApplicable
                }
              },
              updatedAt: now,
              updatedBy: userName
            },
            { merge: true }
          );
        });
      } else if (task.source === 'custom') {
        await db.runTransaction(async (transaction) => {
          const ref = db.collection('fieldTracking').doc(task.eventId);
          const snap = await transaction.get(ref);
          const data = snap.data() || {};
          const customTasks = Array.isArray(data.customTasks) ? data.customTasks : [];
          const updatedTasks = customTasks.map((customTask: any) =>
            customTask.id === task.taskId
              ? {
                  ...customTask,
                  finalized: nextNotApplicable ? false : customTask.finalized ?? false,
                  finalizedBy: nextNotApplicable ? null : customTask.finalizedBy ?? null,
                  finalizedAt: nextNotApplicable ? null : customTask.finalizedAt ?? null,
                  notApplicable: nextNotApplicable
                }
              : customTask
          );

          transaction.set(
            ref,
            { customTasks: updatedTasks, updatedAt: now, updatedBy: userName },
            { merge: true }
          );
        });
      } else {
        await db.runTransaction(async (transaction) => {
          const trackingRef = db.collection('fieldTracking').doc(task.eventId);
          const trackingSnap = await transaction.get(trackingRef);
          const trackingData = trackingSnap.data() || {};
          const existingFlexFields = trackingData.flexFields || {};
          const existingTaskRecord = existingFlexFields[task.taskId] || {};

          transaction.set(
            trackingRef,
            {
              flexFields: {
                [task.taskId]: {
                  ...existingTaskRecord,
                  dueDate: existingTaskRecord.dueDate ?? task.dueDate,
                  assignedTo: existingTaskRecord.assignedTo ?? task.assignedTo,
                  notApplicable: nextNotApplicable
                }
              },
              updatedAt: now,
              updatedBy: userName
            },
            { merge: true }
          );

          const eventRef = db.collection('events').doc(task.eventId);
          const eventSnap = await transaction.get(eventRef);
          const eventData = eventSnap.data() || {};
          const flexDetailsFields = Array.isArray(eventData.flexDetailsFields)
            ? eventData.flexDetailsFields
            : eventData.flexDetailsFields
            ? Object.values(eventData.flexDetailsFields)
            : [];
          const updatedFields = flexDetailsFields.map((field: any) =>
            field.id === task.taskId
              ? {
                  ...field,
                  finalized: nextNotApplicable ? false : field.finalized ?? false,
                  finalizedBy: nextNotApplicable ? undefined : field.finalizedBy,
                  finalizedAt: nextNotApplicable ? null : field.finalizedAt ?? null
                }
              : field
          );

          transaction.update(eventRef, { flexDetailsFields: updatedFields });
        });
      }

      dispatch(setLastSaved(now));

      if (nextNotApplicable) {
        try {
          await db.collection(TASK_INDEX_COLLECTION).doc(task.id).delete();
        } catch (err) {
          console.warn('Unable to optimistically remove task index doc', err);
        }
      }
    },
    [db, user]
  );
}
